import { BusinessRuleItemType } from '../constants/globals';
import { ChiroUpJSON } from '../functions/ChiroUpJSON';
import { ListClinic } from './Clinic.type';
import { ClaimResponseEraType, EraType } from './Era.type';
import { IntegrationExchangeType } from './Integration.type';
import { IntegrationInvoice, InvoiceStatusEnum } from './Invoice.type';
import { Patient } from './Patient.type';
import { PatientTransaction } from './PatientTransaction.type';

export type TransactionDebugOptionsInclude = {
  patientTransaction?: boolean;
  patientTransactionItem?: boolean;
  patientTransactionItemInsurance?: boolean;
  patientTransactionItemDiagnosis?: boolean;
  appointment?: boolean;
  encounter?: boolean;
  patient?: boolean;
  invoice?: boolean;
  era?: boolean;
  claimResponseEra?: boolean;
  payorLineItem?: boolean;
  patientInsurance?: boolean;
  appointmentInsurance?: boolean;
  payor?: boolean;
  billingCode?: boolean;
  billingCodePayor?: boolean;
  billingProfile?: boolean;
  billingProfileUser?: boolean;
  provider?: boolean;
  integration?: boolean;
  appointmentGroup?: boolean;
  appointmentGroupItem?: boolean;
  appointmentGroupLog?: boolean;
  orphanedClaimResponseEra?: boolean;
  ePayTransactions?: boolean;
  dejavooTransactions?: boolean;
  claimResponseWriteOff?: boolean;
  claimResponseIncentive?: boolean;
  claimLineEvent?: boolean;
  integrationExchange?: boolean;
  clinic?: boolean;
  clinicLocations?: boolean;
  transactionActivity?: boolean;
  appointmentActivity?: boolean;
};

export type TransactionDebugOptions = {
  include: TransactionDebugOptionsInclude;
};

export type TransactionDebugParams = {
  fn?: string;
  clinicId?: number;
  billingKey?: string;
  salesforceCase?: string;
  mondayStory?: string;
  groupExpand?: boolean;
  includePayments?: boolean;
  options?: TransactionDebugOptions;
};

export type DejavooDiagnosticRunStatus = 'running' | 'complete' | 'failed';

export type DejavooDiagnosticCode =
  | 'pending'
  | 'processor_approved_missing_ledger'
  | 'processor_approved_needs_review'
  | 'processor_confirmed_absent'
  | 'processor_not_approved'
  | 'inconclusive'
  | 'missing_identity'
  | 'fixed';

export type DejavooDiagnosticSeverity = 'critical' | 'warning' | 'info' | 'resolved';

export type DejavooDiagnosticAutofix = 'reconcile_payment' | 'dismiss_transaction' | null;

export type DejavooDiagnosticFinding = {
  transactionId: number;
  clinicId: number;
  amount: number;
  tpn: string | null;
  externalReference: string | null;
  transactionStatus: string;
  createdAt: number;
  diagnosis: DejavooDiagnosticCode;
  severity: DejavooDiagnosticSeverity;
  summary: string;
  explanation: string;
  recommendedAction: string;
  processorMessage?: string;
  processorResultCode?: string;
  processorStatusCode?: string;
  autofix: DejavooDiagnosticAutofix;
  autofixBlockedReason?: string;
  checkedAt?: number;
  deepDiagnosedAt?: number;
  fixedAt?: number;
  fixedBillingKey?: string;
};

export type DejavooDiagnosticRun = {
  id: string;
  status: DejavooDiagnosticRunStatus;
  findings: DejavooDiagnosticFinding[];
  totalCount: number;
  processedCount: number;
  startedAt: number;
  completedAt?: number | null;
  nextAllowedAt: number;
  errorMessage?: string | null;
  cached: boolean;
};

export type DejavooDiagnosticResponse = {
  run: DejavooDiagnosticRun | null;
  retryAfterMs?: number;
};

export type DejavooDiagnosticRequest =
  | { fn: 'get' }
  | { fn: 'start' }
  | { fn: 'process'; runId: string }
  | { fn: 'deepDiagnose'; runId: string; transactionId: number; clinicId: number }
  | {
      fn: 'autofix';
      runId: string;
      transactionId: number;
      clinicId: number;
      action: Exclude<DejavooDiagnosticAutofix, null>;
      confirmed: true;
    };

export type ClinicPatientReindexRequest = {
  clinicId: number;
  previousClinicId?: number;
  startAfterId?: string;
  batchSize?: number;
};

export type ClinicPatientReindexResponse = {
  totalPatients: number;
  processed: number;
  indexed: number;
  previousRoutingProcessed: number;
  failed: number;
  hasMore: boolean;
  nextStartAfterId?: string;
  indexedPatientCount?: number;
  countVerified?: boolean;
};

export type EraPanelParams = {
  fn?: string;
  billingKey?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  eraId?: string;
  options?: TransactionDebugOptions;
};

export type TransactionDebugAnalysisType = {
  message: string;
};

export type TransactionDebugTableType = {
  data: any;
  analysis: TransactionDebugAnalysisType[];
};

export type TransactionDebugResponse = {
  patientTransaction?: TransactionDebugTableType;
  patientTransactionItem?: TransactionDebugTableType;
  patientTransactionItemInsurance?: TransactionDebugTableType;
  patientTransactionItemDiagnosis?: TransactionDebugTableType;
  appointment?: TransactionDebugTableType;
  encounter?: TransactionDebugTableType;
  patient?: TransactionDebugTableType;
  invoice?: TransactionDebugTableType;
  era?: TransactionDebugTableType;
  claimResponseEra?: TransactionDebugTableType;
  payorLineItem?: TransactionDebugTableType;
  payorLineItemAdjustment?: TransactionDebugTableType;
  patientInsurance?: TransactionDebugTableType;
  appointmentInsurance?: TransactionDebugTableType;
  payor?: TransactionDebugTableType;
  billingCode?: TransactionDebugTableType;
  billingCodePayor?: TransactionDebugTableType;
  billingProfile?: TransactionDebugTableType;
  billingProfileUser?: TransactionDebugTableType;
  provider?: TransactionDebugTableType;
  integration?: TransactionDebugTableType;
  appointmentGroup?: TransactionDebugTableType;
  appointmentGroupItem?: TransactionDebugTableType;
  appointmentGroupLog?: TransactionDebugTableType;
  orphanedClaimResponseEra?: TransactionDebugTableType;
  ePayTransactions?: TransactionDebugTableType;
  dejavooTransactions?: TransactionDebugTableType;
  claimResponseWriteOff?: TransactionDebugTableType;
  claimResponseIncentive?: TransactionDebugTableType;
  claimLineEvent?: TransactionDebugTableType;
  integrationExchange?: TransactionDebugTableType;
  clinic?: TransactionDebugTableType;
  clinicLocations?: TransactionDebugTableType;
  transactionActivity?: TransactionDebugTableType;
  appointmentActivity?: TransactionDebugTableType;
  payments?: TransactionDebugPayment[];
  exchangeIdSources?: TransactionDebugExchangeIdSources;
};

export type TransactionDebugTimelineEntry = {
  timestamp: number;
  source: string;
  event: string;
  detail?: string;
  billingKey?: string;
};

export type TransactionDebugMergedAnalysis = {
  source: string;
  message: string;
  billingKey?: string;
};

export type TransactionDebugPayment = {
  transaction: any;
  items: any[];
};

export type TransactionDebugExchangeIdSources = Record<string, string>;

export type TransactionDebugResult = {
  groupInfo?: { groupId: number; clinicId: number; billingKeys: string[] };
  appointmentGroup?: TransactionDebugTableType;
  appointmentGroupItem?: TransactionDebugTableType;
  appointmentGroupLog?: TransactionDebugTableType;
  clinic?: TransactionDebugTableType;
  clinicLocations?: TransactionDebugTableType;
  byBillingKey: Record<string, TransactionDebugResponse>;
  timeline?: TransactionDebugTimelineEntry[];
  analysis?: TransactionDebugMergedAnalysis[];
  salesforceCase?: string;
  mondayStory?: string;
};

export type InvoicePanelParams = {
  fn?: string;
  options: {
    convertIssuesToNotes: boolean;
  };
  sInvoices?: string;
  invoices?: string[];
  finalStatus?: InvoiceStatusEnum;
  currentStatus?: InvoiceStatusEnum;
};

export type BillingKeyPanelParams = {
  fn?: string;
  sBillingKeys?: string;
  billingKeys?: string[];
  clinicId?: number;
  limit?: number;
  batchSize?: number;
};

export type EraPanelConfirmParams = {
  fn?: string;
  ids?: (string | number)[];
  tasks?: any[];
  storyId?: string;
  action?: 'delete' | 'unpost';
};

export type InvoicePanelResponseItem = {
  invoice: IntegrationInvoice | null | undefined;
  success: boolean;
  messages: string[];
};

export type InvoicePanelResponse = {
  items: InvoicePanelResponseItem[];
};

export type EraPanelResponse = {
  era?: EraType;
  clinic?: ListClinic;
  items?: ClaimResponseEraType[];
  exchanges?: IntegrationExchangeType[];
  issues?: BusinessRuleItemType[];
  purchases?: PatientTransaction[];
  payments?: PatientTransaction[];
  tasks?: any[];
};

export type DevtoolsGetPatientResponse = {
  items: Patient[];
};

export type DevtoolsGetBillingPreview = {
  patient: Patient;
  tables: {
    name: string;
    column: string;
    hits: number;
  }[];
};

export type DevtoolsGetBillingPreviewResponse = {
  items: DevtoolsGetBillingPreview[];
};

export interface SearchFormInputs {
  fname: string;
  lname: string;
  clinicId: number;
}
export interface GetPreviewInputs {
  clinicId: number;
  ids: string[];
}

/**
 * This has the names of the sources and the order IN WHICH THEY SHOULD
 * BE VISITED. This is important to the service as later data sources
 * depend on earlier. However, for humans, we probably want to sort them.
 * by the label or the key.
 */
export const TransactionDebugSources = [
  {
    key: 'patientTransaction',
    checked: true,
    label: 'Patient Transaction',
  },
  {
    key: 'patientTransactionItem',
    checked: true,
    label: 'Patient Transaction Item',
  },
  {
    key: 'patientTransactionItemDiagnosis',
    checked: true,
    label: 'Patient Transaction Item Diagnosis',
  },
  {
    key: 'patientTransactionItemInsurance',
    checked: true,
    label: 'Patient Transaction Item Insurance',
  },
  {
    key: 'appointment',
    checked: true,
    label: 'Appointment',
  },
  {
    key: 'encounter',
    checked: true,
    label: 'Encounter',
  },
  {
    key: 'patient',
    checked: true,
    label: 'Patient',
  },
  {
    key: 'invoice',
    checked: true,
    label: 'Invoice',
  },
  {
    key: 'era',
    checked: true,
    label: 'ERA',
  },
  {
    key: 'claimResponseEra',
    checked: true,
    label: 'Claim Response ERA',
  },
  {
    key: 'payorLineItem',
    checked: true,
    label: 'Payor Line Item',
  },
  {
    key: 'payorLineItemAdjustment',
    checked: true,
    label: 'Payor Line Item Adjustment',
  },
  {
    key: 'patientInsurance',
    checked: true,
    label: 'Patient Insurance',
  },
  {
    key: 'appointmentInsurance',
    checked: true,
    label: 'Appointment Insurance',
  },
  {
    key: 'payor',
    checked: false,
    label: 'Payor',
  },
  {
    key: 'billingCode',
    checked: false,
    label: 'Billing Code',
  },
  {
    key: 'billingCodePayor',
    checked: false,
    label: 'Billing Code Payor',
  },
  {
    key: 'billingProfile',
    checked: false,
    label: 'Billing Profile',
  },
  {
    key: 'billingProfileUser',
    checked: false,
    label: 'Billing Profile User',
  },
  {
    key: 'provider',
    checked: false,
    label: 'Provider',
  },
  {
    key: 'integration',
    checked: false,
    label: 'Integration',
  },
  {
    key: 'appointmentGroup',
    checked: false,
    label: 'Appointment Group',
  },
  {
    key: 'appointmentGroupItem',
    checked: false,
    label: 'Appointment Group Item',
  },
  {
    key: 'appointmentGroupLog',
    checked: false,
    label: 'Appointment Group Log',
  },
  {
    key: 'orphanedClaimResponseEra',
    checked: false,
    label: 'Orphaned Claim Response ERA',
  },
  {
    key: 'ePayTransactions',
    checked: false,
    label: 'ePay Transactions',
  },
  {
    key: 'dejavooTransactions',
    checked: false,
    label: 'Dejavoo Transactions',
  },
  {
    key: 'claimResponseWriteOff',
    checked: false,
    label: 'Claim Response Write-Off',
  },
  {
    key: 'claimResponseIncentive',
    checked: false,
    label: 'Claim Response Incentive',
  },
  {
    key: 'claimLineEvent',
    checked: false,
    label: 'Claim Line Event',
  },
  {
    key: 'integrationExchange',
    checked: false,
    label: 'Integration Exchange',
  },
  {
    key: 'clinic',
    checked: true,
    label: 'Clinic',
  },
  {
    key: 'clinicLocations',
    checked: true,
    label: 'Clinic Locations',
  },
  {
    key: 'transactionActivity',
    checked: true,
    label: 'Transaction Activity (DynamoDB)',
  },
  {
    key: 'appointmentActivity',
    checked: true,
    label: 'Appointment Activity (DynamoDB)',
  },
];

export const TransactionDebugSourcesMap = TransactionDebugSources.reduce(
  (acc, source) => {
    acc[source.key] = source;

    return acc;
  },
  {} as Record<string, (typeof TransactionDebugSources)[0]>
);

const sortedTransactionDebugSources = ChiroUpJSON.clone(TransactionDebugSources).sort(
  (a: any, b: any) => a.label.localeCompare(b.label)
);
const columnSize = Math.ceil(sortedTransactionDebugSources.length / 4);
export const TransactionDebugSourceColumns = Array.from({ length: 4 }, (_, i) =>
  sortedTransactionDebugSources.slice(i * columnSize, (i + 1) * columnSize)
);
